import { z } from 'zod';
import { ReservedPlanId } from './subscriptions.js';
/**
 * The deployment a license key is valid for.
 *
 * Logto signs one key per environment for the same license, identical except for this claim, so a
 * customer can run their staging or CI deployment without sharing the production key. Which key is
 * installed where is the customer's declaration; Logto does not verify it.
 */
export var LicenseEnv;
(function (LicenseEnv) {
    LicenseEnv["Production"] = "production";
    LicenseEnv["NonProduction"] = "non-production";
})(LicenseEnv || (LicenseEnv = {}));
/** The plans a license key can grant. A Cloud-only plan is never a valid license `plan`. */
export const selfHostedPlanIds = Object.freeze([
    ReservedPlanId.SelfHostedPro,
    ReservedPlanId.SelfHostedEnterprise,
]);
export const licenseQuotaGuard = z.object({
    hideLogtoBranding: z.boolean(),
    bringYourUi: z.boolean(),
    idpInitiatedSso: z.boolean(),
    consoleCollaboration: z.boolean(),
    mandatoryMfa: z.boolean(),
    hostedEmail: z.boolean(),
    samlApplicationsLimit: z.number().int().nonnegative().nullable(),
});
/**
 * The entitlements a license key grants, as they appear in its payload.
 *
 * Every field is optional: a key only carries what it changes, and an absent field falls back to
 * {@link ossDefaultQuota}. Keeping it partial means a key signed before a new entitlement existed
 * still verifies on a newer Logto, and the missing entitlement stays locked rather than breaking
 * the whole license. Use {@link resolveLicenseQuota} to apply the overrides.
 */
export const licenseQuotaOverridesGuard = licenseQuotaGuard.partial();
export const licenseUsageLimitsGuard = z.object({
    mau: z.number().int().nonnegative().nullable().optional(),
    productionDeployments: z.number().int().nonnegative().nullable().optional(),
    monthlyAccessTokens: z.number().int().nonnegative().nullable().optional(),
});
/** The metrics a usage limit or warning may refer to. */
export var LicenseUsageMetric;
(function (LicenseUsageMetric) {
    LicenseUsageMetric["Mau"] = "mau";
    LicenseUsageMetric["ProductionDeployments"] = "productionDeployments";
    LicenseUsageMetric["MonthlyAccessTokens"] = "monthlyAccessTokens";
})(LicenseUsageMetric || (LicenseUsageMetric = {}));
export const licenseMonthlyUsageGuard = z.object({
    lastMonth: z.number().int().nonnegative(),
    monthToDate: z.number().int().nonnegative(),
});
export const licenseUsageGuard = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    mau: licenseMonthlyUsageGuard,
    monthlyAccessTokens: licenseMonthlyUsageGuard,
    tenants: z.number().int().nonnegative(),
});
export const licenseUsageWarningGuard = z.object({
    metric: z.string().min(1),
    limit: z.number().nonnegative(),
    value: z.number().nonnegative(),
});
export const licensePayloadGuard = z.object({
    plan: z.enum(selfHostedPlanIds),
    env: z.nativeEnum(LicenseEnv),
    customerId: z.string(),
    licenseId: z.string(),
    iat: z.number().int().nonnegative(),
    exp: z.number().int().nonnegative(),
    quota: licenseQuotaOverridesGuard,
    // Unlike the enforced `quota`, a limit that does not parse is dropped rather than rejecting the
    // whole key: an advisory field must never take a customer's entitlements away.
    usageLimits: z.preprocess((claim) => (licenseUsageLimitsGuard.safeParse(claim).success ? claim : undefined), licenseUsageLimitsGuard.optional()),
});
/**
 * The entitlements of a self-hosted instance with no license key installed, i.e. today's OSS.
 *
 * Every feature is locked and SAML applications keep the long-standing cap of 3. Core falls back to
 * this when no valid license is installed, and Console displays it, so there is exactly one
 * definition of "self-hosted, no license".
 */
export const ossDefaultQuota = Object.freeze({
    hideLogtoBranding: false,
    bringYourUi: false,
    idpInitiatedSso: false,
    consoleCollaboration: false,
    mandatoryMfa: false,
    hostedEmail: false,
    samlApplicationsLimit: 3,
});
/**
 * Resolve the effective entitlements of a self-hosted instance by applying a license key's
 * overrides on top of {@link ossDefaultQuota}. Without a key, the OSS defaults are the answer.
 *
 * Core and Console both call this so they can never disagree on what a license grants. The fields
 * are applied one by one on purpose: `samlApplicationsLimit` is nullable and `null` means
 * unlimited, so a `??` merge would silently turn an unlimited license back into the OSS cap.
 */
export const resolveLicenseQuota = (overrides = {}) => ({
    hideLogtoBranding: overrides.hideLogtoBranding ?? ossDefaultQuota.hideLogtoBranding,
    bringYourUi: overrides.bringYourUi ?? ossDefaultQuota.bringYourUi,
    idpInitiatedSso: overrides.idpInitiatedSso ?? ossDefaultQuota.idpInitiatedSso,
    consoleCollaboration: overrides.consoleCollaboration ?? ossDefaultQuota.consoleCollaboration,
    mandatoryMfa: overrides.mandatoryMfa ?? ossDefaultQuota.mandatoryMfa,
    hostedEmail: overrides.hostedEmail ?? ossDefaultQuota.hostedEmail,
    samlApplicationsLimit: overrides.samlApplicationsLimit === undefined
        ? ossDefaultQuota.samlApplicationsLimit
        : overrides.samlApplicationsLimit,
});
